import * as Ably from 'ably';
import Echo from '@ably/laravel-echo';

import { ENVIRONMENT } from '../../../constants';
import { Nullable } from '../../../types';

const AUTH_ENDPOINT = `${ENVIRONMENT.apiUrl}/broadcasting/auth`;

// @ably/laravel-echo reads the Ably client library from the global scope. This
// runs on the way into createEcho rather than at module scope: the module is
// reachable from the workflow block config, which the node-side e2e runner
// imports, and `window` does not exist there.
const publishAblyGlobal = () => {
  (window as unknown as { Ably: typeof Ably }).Ably = Ably;
};

/**
 * Creates a native Ably-backed Laravel Echo connection.
 *
 * One authenticated connection serves both private and presence channels.
 *
 * Auth is dynamic: `requestTokenFn` is invoked by the connector on the initial
 * auth and on every token renewal, and reads the current headers via
 * `getAuthHeaders()` each time. A rotated JWT is therefore picked up without
 * recreating the connection. The clientId is assigned server-side by
 * `/broadcasting/auth` and is immutable for the life of the connection, so a new
 * instance must be created when the identity (user) changes; see
 * useWebsocketManagement.
 */
export const createEcho = (
  getAuthHeaders: () => Record<string, string>,
): Echo => {
  publishAblyGlobal();

  return new Echo({
    broadcaster: 'ably',
    authEndpoint: AUTH_ENDPOINT,
    requestTokenFn: async (
      channelName: string,
      existingToken: Nullable<string>,
    ) => {
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          channel_name: channelName,
          token: existingToken,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Broadcasting auth request failed with status ${response.status}`,
        );
      }

      return response.json();
    },
  });
};
